import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, RefreshCw, Copy, Loader2, Eye, EyeOff } from "lucide-react";

export const AdminSecurity = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQr, setShowQr] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    api.get("/admin/security")
      .then((r) => setInfo(r.data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const regenerate = async () => {
    if (!window.confirm("Generate a NEW backup code? Your old backup code will stop working immediately.")) return;
    setRegenerating(true);
    try {
      const { data } = await api.post("/admin/security/recovery-code/regenerate");
      setNewCode(data.recovery_code);
      setInfo((i) => ({ ...i, has_recovery_code: true }));
      toast.success("New backup code generated, write it down!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRegenerating(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (loading) {
    return <p className="py-20 text-center text-sm tracking-widest uppercase text-white/60">Loading...</p>;
  }
  if (!info) return null;

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-security">
      {/* Two-factor authentication */}
      <div className="border border-white/12 bg-[#0B1024] p-6 sq-lg">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} className="text-electric" />
          <h3 className="font-display font-bold uppercase text-white tracking-tight">Two-factor authentication</h3>
        </div>
        <div className="flex items-center gap-2 mb-5">
          <span className={`inline-block w-2 h-2 rounded-full ${info.totp_enabled ? "bg-emerald-400" : "bg-amber-400"}`} />
          <span className="text-sm text-white/70" data-testid="security-2fa-status">
            {info.totp_enabled ? "Active, authenticator enrolled" : "Not enrolled yet, scan the QR below to set up"}
          </span>
        </div>
        <button
          onClick={() => setShowQr((v) => !v)}
          className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold"
          data-testid="security-toggle-qr"
        >
          {showQr ? <EyeOff size={13} /> : <Eye size={13} />} {showQr ? "Hide" : "Show"} setup QR
        </button>
        {showQr && info.qr && (
          <div className="mt-5" data-testid="security-qr-block">
            <p className="text-sm text-white/60 mb-3 leading-relaxed">
              In Google Authenticator / Authy, tap ➕ → <b className="text-white/80">Scan a QR code</b> and point it here:
            </p>
            <img src={info.qr} alt="2FA QR code" className="w-44 h-44 bg-white p-2 sq-md" data-testid="security-qr" />
          </div>
        )}
      </div>

      {/* Backup recovery code */}
      <div className="border border-white/12 bg-[#0B1024] p-6 sq-lg">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={18} className="text-electric" />
          <h3 className="font-display font-bold uppercase text-white tracking-tight">Backup recovery code</h3>
        </div>
        <p className="text-sm text-white/60 mb-5 leading-relaxed">
          A permanent code that always logs you in if you lose your phone. Generating a new one{" "}
          <strong className="text-white">immediately invalidates the previous code</strong>.{" "}
          {info.has_recovery_code ? "A backup code is currently set." : "No backup code set yet."}
        </p>

        {newCode && (
          <div className="border border-electric/40 bg-electric/10 p-5 sq-md mb-5" data-testid="security-new-code-block">
            <p className="text-[10px] tracking-[0.2em] uppercase text-electric mb-3">
              Your new backup code, write it down now
            </p>
            <div className="flex items-center gap-3">
              <code className="text-xl font-bold text-white tracking-[0.15em]" data-testid="security-new-code">{newCode}</code>
              <button onClick={() => copy(newCode)} className="text-white/60 hover:text-electric transition-colors duration-200" data-testid="security-copy-code">
                <Copy size={16} />
              </button>
            </div>
            <p className="text-[11px] text-white/50 mt-3">This is the only time it's shown in full. Store it somewhere safe.</p>
          </div>
        )}

        <button
          onClick={regenerate}
          disabled={regenerating}
          className="btn-primary inline-flex items-center gap-2 px-5 py-3 text-[10px] tracking-[0.2em] uppercase font-bold disabled:opacity-50"
          data-testid="security-regenerate-code"
        >
          {regenerating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          {info.has_recovery_code ? "Regenerate backup code" : "Generate backup code"}
        </button>
      </div>
    </div>
  );
};
